import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { GetProfitLoss } from "../../store/slices/reports";
import { toast } from "react-hot-toast";
import Loader from "../../common/Loader";
import AdminLayout from "../AdminLayout";
import moment from "moment";
import { API_BASE_URL, API_PATHS } from "../../utils/constants/api.constants";

const ProfitAndLoss = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [profitLoss, setProfitLoss] = useState([]);
  const user = JSON.parse(localStorage.getItem("user"));
  const Name = user?.data?.company_name;
  const profile_id = user?.data?.profile_id;

  // Load saved financial year and dates from localStorage
  const loadSavedDates = () => {
    const savedFinancialYear = localStorage.getItem("selectedFinancialYear");

    // Ensure financial year is always correctly structured
    let startYear, endYear;
    if (savedFinancialYear && savedFinancialYear.includes("-")) {
      [startYear, endYear] = savedFinancialYear.split("-");
    } else {
      // Default to the current financial year
      const currentYear = new Date().getFullYear();
      startYear = currentYear - 1; // Example: 2024 (for 2024-2025)
      endYear = currentYear; // Example: 2025 (for 2024-2025)
    }

    return {
      financialYear: savedFinancialYear || `${startYear}-${endYear}`,
      fromDate: localStorage.getItem("savedFromDate") || `${startYear}-04-01`,
      toDate: localStorage.getItem("savedToDate") || `${endYear}-03-31`,
      minDate: `${startYear}-04-01`,
      maxDate: `${endYear}-03-31`,
    };
  };

  const {
    fromDate: initialFromDate,
    toDate: initialToDate,
    minDate,
    maxDate,
    financialYear: initialFinancialYear,
  } = loadSavedDates();

  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(initialToDate);
  const [financialYear, setFinancialYear] = useState(initialFinancialYear);

  useEffect(() => {
    if (!financialYear) return;

    localStorage.setItem("selectedFinancialYear", financialYear);

    let startYear, endYear;
    if (financialYear.includes("-")) {
      [startYear, endYear] = financialYear.split("-");
    } else {
      return; // Exit if financial year is not valid
    }

    const savedFromDate =
      localStorage.getItem("savedFromDate") || `${startYear}-04-01`;
    const savedToDate =
      localStorage.getItem("savedToDate") || `${endYear}-03-31`;

    setFromDate(savedFromDate);
    setToDate(savedToDate);

    if (!localStorage.getItem("savedFromDate"))
      localStorage.setItem("savedFromDate", savedFromDate);
    if (!localStorage.getItem("savedToDate"))
      localStorage.setItem("savedToDate", savedToDate);
  }, [financialYear]);

  useEffect(() => {
    handleProfitLoss();
  }, [financialYear]);

  const handleProfitLoss = async () => {
    const newItem = {
      profile_id: profile_id,
      from_date: fromDate, // Ensure from_date is sent
      to_date: toDate, // Ensure to_date is sent
    };

    setIsLoading(true);

    try {
      const data = await dispatch(GetProfitLoss(newItem)).unwrap();

      setProfitLoss(data?.data || []); // Update profitLoss with the response
    } catch (error) {
      console.error("Error fetching profit/loss data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetDates = async () => {
    const storedFinancialYear = localStorage.getItem("selectedFinancialYear"); // Example: "2022-2023"

    let startYear, endYear;

    if (storedFinancialYear) {
      // Extract the start and end year from stored financial year
      [startYear, endYear] = storedFinancialYear.split("-");
    } else {
      // If not found, determine the current financial year dynamically
      const today = new Date();
      startYear =
        today.getMonth() + 1 >= 4
          ? today.getFullYear()
          : today.getFullYear() - 1;
      endYear = parseInt(startYear) + 1;
    }

    const defaultFromDate = `${startYear}-04-01`;
    const defaultToDate = `${endYear}-03-31`;
    console.log(defaultFromDate, defaultToDate);
    // Update state
    setFromDate(defaultFromDate);
    setToDate(defaultToDate);

    localStorage.setItem("savedFromDate", defaultFromDate);
    localStorage.setItem("savedToDate", defaultToDate);
    // Create newItem with updated financial year

    // Call handleProfitLoss with updated values directly
    const newItem = {
      profile_id: profile_id,
      from_date: defaultFromDate, // Use updated dates here
      to_date: defaultToDate, // Use updated dates here
    };

    setIsLoading(true);

    try {
      const data = await dispatch(GetProfitLoss(newItem)).unwrap();

      setProfitLoss(data?.data || []); // Update Profit/Loss with the response
    } catch (error) {
      console.error("Error fetching profit/loss data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  function formatDateRange(startDate, endDate) {
    const options = { day: "2-digit", month: "long", year: "numeric" };

    const formattedStartDate = new Date(startDate).toLocaleDateString(
      "en-GB",
      options
    );
    const formattedEndDate = new Date(endDate).toLocaleDateString(
      "en-GB",
      options
    );

    return `${formattedStartDate} to ${formattedEndDate}`;
  }

  const handlePDF = (item) => {
    const data = `${API_BASE_URL}${API_PATHS.profitandlossPdf}/${fromDate}/${toDate}/${profile_id}`;
    navigate("/Pdf-Viewer", { state: { data } });
  };

  const handleFromDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate >= minDate && newDate <= maxDate) {
      setFromDate(newDate);
      localStorage.setItem("savedFromDate", newDate);
    } else {
      toast.error(
        `Select a date within the financial year ${formatFinancialYear(
          minDate,
          maxDate
        )}`
      );
    }
  };

  const handleToDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate >= minDate && newDate <= maxDate) {
      setToDate(newDate);
      localStorage.setItem("savedToDate", newDate);
    } else {
      toast.error(
        `Select a date within the financial year ${formatFinancialYear(
          minDate,
          maxDate
        )}`
      );
    }
  };

  const formatFinancialYear = (startDate, endDate) => {
    return `(${startDate.split("-").reverse().join("-")} to ${endDate
      .split("-")
      .reverse()
      .join("-")})`;
  };
  const goToGroupLedger = (groupName) => {
    if (groupName == "Closing Stock") {
      navigate("/reports/stocksummary");
    } else {
      navigate(`/group-ledgers/${encodeURIComponent(groupName)}`);
    }
  };
  
  // Helpers to compute accurate bottom totals for both sides
  const parseAmount = (val) => {
    const num =
      typeof val === "number"
        ? val
        : parseFloat((val || "0").toString().replace(/,/g, ""));
    return isNaN(num) ? 0 : num;
  };
  const sumAmountList = (list) =>
    Array.isArray(list)
      ? list.reduce((acc, it) => acc + parseAmount(it?.amount), 0)
      : 0;

  const grossPL = profitLoss?.income_statement?.["Gross Profit/Loss"]; // { description: 'Gross Profit'|'Gross Loss', amount }
  const netPL = profitLoss?.income_statement?.["Net Profit/Loss"]; // { description: 'Net Profit'|'Net Loss', amount }

  const grossAmt = parseAmount(grossPL?.amount);
  const netAmt = parseAmount(netPL?.amount);
  const isGrossProfit = grossPL?.description === "Gross Profit";
  const isGrossLoss = grossPL?.description === "Gross Loss";
  const isNetProfit = netPL?.description === "Net Profit";
  const isNetLoss = netPL?.description === "Net Loss";

  const indirectExpensesTotal = sumAmountList(
    profitLoss?.income_statement?.["Indirect Expenses"]
  );
  const indirectIncomeTotal = sumAmountList(
    profitLoss?.income_statement?.["Indirect Income"]
  );

  // Bottom totals should balance and reflect lower-section totals
  const leftBottomTotal =
    indirectExpensesTotal + (isGrossLoss ? grossAmt : 0) + (isNetProfit ? netAmt : 0);
  const rightBottomTotal =
    indirectIncomeTotal + (isGrossProfit ? grossAmt : 0) + (isNetLoss ? netAmt : 0);
  return (
    <AdminLayout>
      {isLoading && <Loader />}
      <div className="row content-body">
        <div className="container-fluid">
          <div className="page-header">
            <div>
              <h2 class="main-content-title tx-24 mg-b-5">Profit and Loss</h2>
              <ol class="breadcrumb">
                <li class="breadcrumb-item">
                  <a href="#">Reports</a>
                </li>
                <li class="breadcrumb-item active" aria-current="page">
                  Profit and Loss
                </li>
              </ol>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-12">
              <div class="row">
                <div class="col-md-12">
                  <div class="card custom-card">
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3 form-inline">
                          <div class="form-group">
                            <label class="">From Date</label>
                            <input
                              class="form-control"
                              type="date"
                              value={fromDate}
                              onChange={handleFromDateChange}
                              min={minDate}
                              max={maxDate}
                            />
                          </div>
                        </div>
                        <div class="col-md-3 form-inline">
                          <div class="form-group">
                            <label class="">To Date</label>
                            <input
                              class="form-control"
                              type="date"
                              value={toDate}
                              onChange={handleToDateChange}
                              min={minDate}
                              max={maxDate}
                            />
                          </div>
                        </div>
                        <div class="col-md-5 form-inline">
                          <div class="form-group">
                            <button
                              type="submit"
                              class="btn btn-default"
                              onClick={() => handleProfitLoss()}
                            >
                              Submit
                            </button>
                          </div>
                          <div class="form-group">
                            <button
                              type="button"
                              className="btn btn-default"
                              onClick={resetDates}
                            >
                              Reset Date
                            </button>
                          </div>
                          <div class="form-group">
                            <button
                              type="button"
                              className="btn btn-default"
                              onClick={handlePDF}
                            >
                              Profit and Loss PDF
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-12">
                  <div className="card custom-card">
                    <div className="card-body">
                      {/* Trial Balance Table */}
                      <div
                        style={{ textAlign: "center", marginBottom: "20px" }}
                      >
                        <h3
                          style={{ fontWeight: "700" }}
                          className="text-capitalize"
                        >
                          {Name}
                        </h3>
                        <h4 style={{ fontWeight: "700" }}>Profit & Loss</h4>
                        <p>{formatDateRange(fromDate, toDate)}</p>
                      </div>

                      <table
                        className="table table-borderless"
                        style={{ width: "100%", fontSize: "12px" }}
                      >
                        <tbody>
                          <tr>
                            <td style={{ border: "1px solid" }}>
                              <div className="d-flex justify-content-between">
                                <span className="font-weight-bold">
                                  Particulars
                                </span>
                                <span>
                                  as to {moment(toDate).format("DD-MM-YYYY")}
                                </span>
                              </div>
                            </td>
                            <td style={{ border: "1px solid" }}>
                              <div className="d-flex justify-content-between">
                                <span className="font-weight-bold">
                                  Particulars
                                </span>
                                <span>
                                  as at {moment(toDate).format("DD-MM-YYYY")}
                                </span>
                              </div>
                            </td>
                          </tr>

                          <tr>
                            <td style={{ border: "1px solid" }}>
                              <div className="mb-2">
                                {profitLoss?.income_statement?.Expenses?.map(
                                  (expense, index) => {
                                    // Calculate the total amount for the group by summing up amounts in the group's items (if applicable)
                                    const groupTotal = parseFloat(
                                      expense?.amount?.replace(/,/g, "") || 0
                                    );

                                    return (
                                      <React.Fragment key={index}>
                                        <div
                                          className="d-flex justify-content-between font-weight-bold"
                                          onClick={() =>
                                            goToGroupLedger(expense?.group)
                                          }
                                          style={{ cursor: "pointer" }}
                                        >
                                          <b>{expense?.group}</b>
                                          <span>
                                            ₹
                                            {groupTotal.toLocaleString(
                                              "en-IN",
                                              { minimumFractionDigits: 2 }
                                            )}
                                          </span>
                                        </div>
                                      </React.Fragment>
                                    );
                                  }
                                )}
                              </div>

                              {profitLoss?.income_statement?.[
                                "Gross Profit/Loss"
                              ]?.description == "Gross Profit" ? (
                                <div className="d-flex justify-content-between">
                                  <b>Gross Profit</b>
                                  <b>
                                    ₹
                                    {parseFloat(
                                      profitLoss?.income_statement?.[
                                        "Gross Profit/Loss"
                                      ]?.amount?.replace(/,/g, "")
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </b>
                                </div>
                              ) : (
                                ""
                              )}

                              <div
                                className="d-flex justify-content-end"
                                style={{
                                  marginBottom: "15px",
                                  paddingTop: "10px",
                                  paddingBottom: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    paddingLeft: "110px",
                                    paddingTop: "10px",
                                    paddingBottom: "10px",
                                    textAlign: "right",
                                    borderTop: "1px solid",
                                    borderBottom: "1px solid",
                                  }}
                                >
                                  ₹
                                  {(
                                    (profitLoss?.income_statement?.[
                                      "Gross Profit/Loss"
                                    ]?.description == "Gross Profit" &&
                                      parseFloat(
                                        profitLoss?.income_statement?.[
                                          "Gross Profit/Loss"
                                        ]?.amount.replace(/,/g, "")
                                      )) +
                                    profitLoss?.income_statement?.Expenses?.reduce(
                                      (acc, expense) =>
                                        acc +
                                        parseFloat(
                                          expense?.amount.replace(/,/g, "")
                                        ),
                                      0
                                    )
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                  })}
                                </div>
                              </div>

                              {profitLoss?.income_statement?.[
                                "Gross Profit/Loss"
                              ]?.description == "Gross Loss" ? (
                                <div className="d-flex justify-content-between">
                                  <b>B / F Loss</b>
                                  <b>
                                    ₹
                                    {parseFloat(
                                      profitLoss?.income_statement?.[
                                        "Gross Profit/Loss"
                                      ]?.amount?.replace(/,/g, "") || 0
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </b>
                                </div>
                              ) : (
                                ""
                              )}

                              {profitLoss?.income_statement?.[
                                "Indirect Expenses"
                              ] && (
                                <div className="mb-2">
                                  {profitLoss.income_statement[
                                    "Indirect Expenses"
                                  ]
                                    // Filter out entries with 0 amount
                                    .filter(
                                      (income) =>
                                        parseFloat(
                                          income?.amount?.replace(/,/g, "") || 0
                                        ) !== 0
                                    )
                                    .map((income, index, arr) => (
                                      <React.Fragment key={index}>
                                        {index === 0 && (
                                          <div
                                            className="d-flex justify-content-between font-weight-bold"
                                            onClick={() =>
                                              goToGroupLedger(income?.group)
                                            }
                                            style={{ cursor: "pointer" }}
                                          >
                                            <b>{income?.group}</b>
                                          </div>
                                        )}
                                        <span className="d-flex justify-content-between">
                                          <span style={{ paddingLeft: "20px" }}>
                                            {income?.ledger}
                                          </span>
                                          <span>
                                            ₹
                                            {parseFloat(
                                              income?.amount?.replace(
                                                /,/g,
                                                ""
                                              ) || 0
                                            ).toLocaleString("en-IN", {
                                              minimumFractionDigits: 2,
                                            })}
                                          </span>
                                        </span>
                                      </React.Fragment>
                                    ))}
                                </div>
                              )}

                              <div
                                className="d-flex justify-content-end"
                                style={{
                                  marginBottom: "15px",
                                  paddingTop: "10px",
                                  paddingBottom: "10px",
                                }}
                              >
                                {((profitLoss?.income_statement?.[
                                  "Indirect Expenses"
                                ]?.description === "Indirect Expenses" &&
                                  parseFloat(
                                    profitLoss?.income_statement?.[
                                      "Indirect Expenses"
                                    ]?.amount?.replace(/,/g, "")
                                  )) ||
                                  0) +
                                  (profitLoss?.income_statement?.[
                                    "Indirect Expenses"
                                  ]?.reduce(
                                    (acc, income) =>
                                      acc +
                                      parseFloat(
                                        income?.amount?.replace(/,/g, "")
                                      ),
                                    0
                                  ) || 0) >
                                0 ? (
                                  <div
                                    style={{
                                      paddingLeft: "110px",
                                      paddingTop: "10px",
                                      paddingBottom: "10px",
                                      textAlign: "right",
                                      borderTop: "1px solid",
                                      borderBottom: "1px solid",
                                    }}
                                  >
                                    ₹
                                    {(
                                      ((profitLoss?.income_statement?.[
                                        "Indirect Expenses"
                                      ]?.description === "Indirect Expenses" &&
                                        parseFloat(
                                          profitLoss?.income_statement?.[
                                            "Indirect Expenses"
                                          ]?.amount?.replace(/,/g, "")
                                        )) ||
                                        0) +
                                      (profitLoss?.income_statement?.[
                                        "Indirect Expenses"
                                      ]?.reduce(
                                        (acc, income) =>
                                          acc +
                                          parseFloat(
                                            income?.amount?.replace(/,/g, "")
                                          ),
                                        0
                                      ) || 0)
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </div>
                                ) : null}
                              </div>

                              {profitLoss?.income_statement?.["Net Profit/Loss"]
                                ?.description == "Net Profit" ? (
                                <div className="d-flex justify-content-between">
                                  <b>Net Profit</b>
                                  <b>
                                    {
                                      profitLoss?.income_statement?.[
                                        "Net Profit/Loss"
                                      ]?.amount
                                    }
                                  </b>
                                </div>
                              ) : (
                                ""
                              )}
                              {/* <div className="d-flex justify-content-between">
                                <span>Indirect Expenses</span>
                                <span>6,000</span>
                              </div> */}
                            </td>

                            <td style={{ border: "1px solid" }}>
                              <div className="mb-2">
                                {profitLoss?.income_statement?.Income?.map(
                                  (income, index) => {
                                    // Parse the numeric value of the income amount
                                    const incomeAmount = parseFloat(
                                      income?.amount?.replace(/,/g, "") || 0
                                    );

                                    return (
                                      <React.Fragment key={index}>
                                        <div
                                          className="d-flex justify-content-between font-weight-bold"
                                          onClick={() =>
                                            goToGroupLedger(income?.group)
                                          }
                                          style={{ cursor: "pointer" }}
                                        >
                                          {/* Display group name and total amount */}
                                          <b>{income?.group}</b>
                                          <span>
                                            ₹
                                            {incomeAmount.toLocaleString(
                                              "en-IN",
                                              { minimumFractionDigits: 2 }
                                            )}
                                          </span>
                                        </div>
                                      </React.Fragment>
                                    );
                                  }
                                )}
                              </div>

                              {profitLoss?.income_statement?.[
                                "Gross Profit/Loss"
                              ]?.description == "Gross Loss" ? (
                                <div className="d-flex justify-content-between">
                                  <b>Gross Loss</b>
                                  <b>
                                    ₹
                                    {parseFloat(
                                      profitLoss?.income_statement?.[
                                        "Gross Profit/Loss"
                                      ]?.amount?.replace(/,/g, "") || 0
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </b>
                                </div>
                              ) : (
                                ""
                              )}

                              <div
                                className="d-flex justify-content-end"
                                style={{
                                  marginBottom: "15px",
                                  paddingTop: "10px",
                                  paddingBottom: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    paddingLeft: "110px",
                                    paddingTop: "10px",
                                    paddingBottom: "10px",
                                    textAlign: "right",
                                    borderTop: "1px solid",
                                    borderBottom: "1px solid",
                                  }}
                                >
                                  ₹
                                  {(
                                    ((profitLoss?.income_statement?.[
                                      "Gross Profit/Loss"
                                    ]?.description === "Gross Loss" &&
                                      parseFloat(
                                        profitLoss?.income_statement?.[
                                          "Gross Profit/Loss"
                                        ]?.amount?.replace(/,/g, "")
                                      )) ||
                                      0) +
                                    (profitLoss?.income_statement?.Income?.reduce(
                                      (acc, income) =>
                                        acc +
                                        parseFloat(
                                          income?.amount?.replace(/,/g, "")
                                        ),
                                      0
                                    ) || 0)
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                  })}
                                </div>
                              </div>
                              {profitLoss?.income_statement?.[
                                "Gross Profit/Loss"
                              ]?.description == "Gross Profit" ? (
                                <div className="d-flex justify-content-between">
                                  <b>B / F Profit</b>
                                  <b>
                                    ₹
                                    {parseFloat(
                                      profitLoss?.income_statement?.[
                                        "Gross Profit/Loss"
                                      ]?.amount?.replace(/,/g, "") || 0
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </b>
                                </div>
                              ) : (
                                ""
                              )}

                              {profitLoss?.income_statement?.[
                                "Indirect Income"
                              ] && (
                                <div className="mb-2">
                                  {profitLoss.income_statement[
                                    "Indirect Income"
                                  ]
                                    // Filter out entries with amount 0
                                    .filter(
                                      (income) =>
                                        parseFloat(
                                          income?.amount?.replace(/,/g, "") || 0
                                        ) !== 0
                                    )
                                    .map((income, index, arr) => (
                                      <React.Fragment key={index}>
                                        {index === 0 && (
                                          <div
                                            className="d-flex justify-content-between font-weight-bold"
                                            onClick={() =>
                                              goToGroupLedger(income?.group)
                                            }
                                            style={{ cursor: "pointer" }}
                                          >
                                            <b>{income?.group}</b>
                                          </div>
                                        )}
                                        <span className="d-flex justify-content-between">
                                          <span style={{ paddingLeft: "20px" }}>
                                            {income.ledger}
                                          </span>
                                          <span>
                                            ₹
                                            {parseFloat(
                                              income?.amount?.replace(
                                                /,/g,
                                                ""
                                              ) || 0
                                            ).toLocaleString("en-IN", {
                                              minimumFractionDigits: 2,
                                            })}
                                          </span>
                                        </span>
                                      </React.Fragment>
                                    ))}
                                </div>
                              )}

                              <div
                                className="d-flex justify-content-end"
                                style={{
                                  marginBottom: "15px",
                                  paddingTop: "10px",
                                  paddingBottom: "10px",
                                }}
                              >
                                {(() => {
                                  const indirectDescAmount =
                                    (profitLoss?.income_statement?.[
                                      "Indirect Income"
                                    ]?.description === "Indirect Income" &&
                                      parseFloat(
                                        profitLoss?.income_statement?.[
                                          "Indirect Income"
                                        ]?.amount?.replace(/,/g, "")
                                      )) || 0;
                                  const indirectSum =
                                    (profitLoss?.income_statement?.[
                                      "Indirect Income"
                                    ]?.reduce(
                                      (acc, income) =>
                                        acc +
                                        parseFloat(
                                          income?.amount?.replace(/,/g, "")
                                        ),
                                      0
                                    ) || 0);
                                  const bfProfit =
                                    (profitLoss?.income_statement?.[
                                      "Gross Profit/Loss"
                                    ]?.description === "Gross Profit" &&
                                      parseFloat(
                                        profitLoss?.income_statement?.[
                                          "Gross Profit/Loss"
                                        ]?.amount?.replace(/,/g, "")
                                      )) || 0;

                                  const total =
                                    indirectDescAmount + indirectSum + bfProfit;
                                  if (total <= 0) return null;
                                  return (
                                    <div
                                      style={{
                                        paddingLeft: "110px",
                                        paddingTop: "10px",
                                        paddingBottom: "10px",
                                        textAlign: "right",
                                        borderTop: "1px solid",
                                        borderBottom: "1px solid",
                                      }}
                                    >
                                      ₹
                                      {total.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>

                              {profitLoss?.income_statement?.["Net Profit/Loss"]
                                ?.description == "Net Loss" ? (
                                <div className="d-flex justify-content-between">
                                  <b>Net Loss</b>
                                  <b>
                                    ₹
                                    {parseFloat(
                                      profitLoss?.income_statement?.[
                                        "Net Profit/Loss"
                                      ]?.amount?.replace(/,/g, "") || 0
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </b>
                                </div>
                              ) : (
                                ""
                              )}
                            </td>
                          </tr>

                          <tr>
                            <td style={{ border: "1px solid" }}>
                              <div className="d-flex justify-content-between">
                                <span>Total</span>
                                <span className="font-weight-bold">
                                  ₹
                                  {leftBottomTotal.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                            </td>

                            <td style={{ border: "1px solid" }}>
                              <div className="d-flex justify-content-between">
                                <span>Total</span>
                                <span className="font-weight-bold">
                                  ₹
                                  {rightBottomTotal.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ProfitAndLoss;
